/**
 * Trade Booking Service
 * Handles API calls for manual trade booking
 */

import { TradeBookingForm, AllocationRow, SplitRow, MultiTradeDraftSummary, MultiTradeDraft, MultiTradeDraftPayload } from '../types/tradeBooking';
import { logger } from '../utils/logger';
import { apiClient } from './api';

/** KAN-216: Potential confirmation match returned after booking a trade */
export interface PotentialConfirmationMatch {
  type: 'single' | 'multiple';
  // Single match fields
  emailId?: string;
  tradeIndex?: number;
  bankTradeNumber?: string;
  counterpartyName?: string;
  receivedDate?: string;
  confidence?: number;
  emailSubject?: string;
  // KAN-260: Companion provisionals to dismiss after definitive match
  companionProvisionals?: Array<{
    emailId: string;
    bankTradeNumber: string;
    tradeIndex: number;
  }>;
  // Multiple match fields
  matchCount?: number;
  message?: string;
}

/**
 * Convert camelCase form data to PascalCase for Firestore
 * @param trade - Trade booking form data in camelCase
 * @returns Trade data in PascalCase format matching CSV upload schema
 */
const convertToPascalCase = (trade: TradeBookingForm): Record<string, any> => {
  // Format dates as YYYY-MM-DD (ISO 8601) for Firestore storage
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Capitalize first letter only (BUY → Buy, SELL → Sell)
  const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Convert FixingReference enum to display value
  const convertFixingReference = (value: string): string => {
    const fixingReferenceMap: Record<string, string> = {
      'USD_OBS': 'USD Obs',
      'UF': 'UF',
      'BLOOMBERG_11AM': 'Bloomberg 11 AM',
      'N/A': 'N/A'
    };
    return fixingReferenceMap[value] || value;
  };

  // Convert SettlementType enum to display value
  const convertSettlementType = (value: string): string => {
    const settlementTypeMap: Record<string, string> = {
      'COMPENSACION': 'Compensación',
      'ENTREGA_FISICA': 'Entrega Física'
    };
    return settlementTypeMap[value] || value;
  };

  const isSpot = trade.tradeType === 'SPOT';

  return {
    // Product type (Spot or Forward)
    ProductType: capitalizeFirst(trade.tradeType), // 'SPOT' → 'Spot', 'FORWARD' → 'Forward'

    // Direction with proper capitalization
    Direction: capitalizeFirst(trade.direction), // 'BUY' → 'Buy', 'SELL' → 'Sell'

    // Currency and amounts
    Currency1: trade.currency1,
    QuantityCurrency1: trade.amount || 0,
    Currency2: trade.currency2,
    QuantityCurrency2: trade.contraAmount || 0,

    // Rates
    Price: trade.price || 0,
    SpotPrice: trade.spotPrice || null,
    ForwardPoints: trade.forwardPoints ?? null,

    // Source channel for audit trail
    SourceChannel: 'WebApp',

    // Dates
    TradeDate: formatDate(trade.tradeDate),
    ValueDate: formatDate(trade.valueDate),
    MaturityDate: formatDate(trade.expiryDate), // ExpiryDate → MaturityDate
    PaymentDate: formatDate(trade.paymentDate),
    // Valuta dates for physical delivery (Entrega Física)
    Currency1PaymentDate: formatDate(trade.currency1PaymentDate),
    Currency2PaymentDate: formatDate(trade.currency2PaymentDate),

    // Parties - Our Entity
    OurParty: trade.ourParty,
    OurPartyId: trade.ourPartyId,
    OurPartyTaxId: trade.ourPartyTaxId,

    // Parties - Counterparty
    CounterpartyName: trade.counterparty,
    CounterpartyId: trade.counterpartyId,
    CounterpartyTaxId: trade.counterpartyTaxId,

    // Forward-specific fields
    FixingReference: isSpot ? 'N/A' : convertFixingReference(trade.fixingReference),
    FixingOffset: trade.fixingOffset,
    SettlementType: convertSettlementType(trade.settlementType),
    // Settlement Currency is N/A when Settlement Type is "Entrega Física"
    SettlementCurrency: trade.settlementType === 'ENTREGA_FISICA' ? 'N/A' : (trade.settlementCurrency || ''),

    // Forward Asiático fields
    IsAverageRateForward: trade.isAverageRateForward || false,
    AverageRateFixingSchedule: (trade.isAverageRateForward && trade.averageRateFixingSchedule)
      ? trade.averageRateFixingSchedule
          .filter(entry => entry.date)
          .map(entry => ({ Date: entry.date }))
      : [],

    // Forward Starting fields
    IsForwardStarting: trade.isForwardStarting || false,
    ForwardStartingFixingSchedule: (trade.isForwardStarting && trade.forwardStartingFixingSchedule)
      ? trade.forwardStartingFixingSchedule
          .filter(entry => entry.date)
          .map(entry => ({ Date: entry.date }))
      : [],
    // SpotFixingReference: spot observation reference for Forward Starting trades (separate from FixingReference)
    SpotFixingReference: trade.isForwardStarting ? convertFixingReference(trade.spotFixingReference) : null,

    // Notes and classification fields
    Notes: trade.notes || '',
    Grouping1: trade.grouping1 || '',
    Grouping2: trade.grouping2 || '',
    Grouping3: trade.grouping3 || '',
    HedgeObject: trade.hedgeObject || '',
  };
};

/**
 * Save trade as draft
 * @param trade - Trade booking form data
 * @param clientId - Client ID
 * @param quoteId - Optional quote ID if trade was booked with a quote
 */
export const saveDraft = async (
  trade: TradeBookingForm,
  clientId: string,
  quoteId?: string
): Promise<{ tradeId: string; tradeNumber: string }> => {
  logger.log('📝 [TradeBookingService] Saving draft:', trade, quoteId ? `with quote ${quoteId}` : '');

  const tradeData = convertToPascalCase(trade);

  // Add quoteId as query parameter if provided
  const url = `/api/v1/clients/${clientId}/trades/book?workflowState=draft${quoteId ? `&quoteId=${quoteId}` : ''}`;

  const response = await apiClient.post<{ trade_id: string; trade_number: string; workflow_state: string }>(
    url,
    tradeData
  );

  logger.log('✅ [TradeBookingService] Draft saved successfully:', response.data);

  return {
    tradeId: response.data.trade_id,
    tradeNumber: response.data.trade_number,
  };
};

/**
 * Submit trade for booking
 * @param trade - Trade booking form data
 * @param clientId - Client ID
 * @param quoteId - Optional quote ID if trade was booked with a quote
 */
export const submitTrade = async (
  trade: TradeBookingForm,
  clientId: string,
  quoteId?: string,
  allocation?: AllocationRow[],
  splits?: SplitRow[]
): Promise<{ tradeId: string; tradeNumber: string; splitTrades?: Array<{ tradeId: string; tradeNumber: string }>; settlementInstruction?: { attempted: boolean; success: boolean; toEmail: string | null; outputFormat: string | null; errorMessage: string | null }; potentialConfirmationMatch?: PotentialConfirmationMatch }> => {
  logger.log('🚀 [TradeBookingService] Submitting trade:', trade, quoteId ? `with quote ${quoteId}` : '', allocation?.length ? `with ${allocation.length} allocation rows` : '', splits?.length ? `with ${splits.length} split rows` : '');

  const tradeData = convertToPascalCase(trade);

  // Include allocation data if provided
  if (allocation && allocation.length > 0) {
    tradeData.allocation = allocation.map(row => ({
      entityId: row.entityId,
      entityName: row.entityName,
      entityTaxId: row.entityTaxId,
      grouping1: row.grouping1,
      grouping2: row.grouping2,
      grouping3: row.grouping3,
      amount: row.amount,
      percentage: row.percentage,
      hedgeObject: row.hedgeObject,
      notes: row.notes,
    }));
  }

  // Include splits data if provided
  if (splits && splits.length > 0) {
    tradeData.splits = splits.map(row => ({
      grouping1: row.grouping1,
      grouping2: row.grouping2,
      grouping3: row.grouping3,
      amount: row.amount,
      percentage: row.percentage,
      hedgeObject: row.hedgeObject,
      notes: row.notes,
    }));
  }

  // Add quoteId as query parameter if provided
  const url = `/api/v1/clients/${clientId}/trades/book?workflowState=booked${quoteId ? `&quoteId=${quoteId}` : ''}`;

  const response = await apiClient.post<{ trade_id: string; trade_number: string; workflow_state: string; split_trades?: Array<{ trade_id: string; trade_number: string }>; settlementInstruction?: { attempted: boolean; success: boolean; toEmail: string | null; outputFormat: string | null; errorMessage: string | null }; potentialConfirmationMatch?: PotentialConfirmationMatch }>(
    url,
    tradeData
  );

  logger.log('✅ [TradeBookingService] Trade submitted successfully:', response.data);

  return {
    tradeId: response.data.trade_id,
    tradeNumber: response.data.trade_number,
    splitTrades: response.data.split_trades?.map(st => ({ tradeId: st.trade_id, tradeNumber: st.trade_number })),
    settlementInstruction: response.data.settlementInstruction,
    potentialConfirmationMatch: response.data.potentialConfirmationMatch,
  };
};

/**
 * Update an existing trade (Draft or Booked only)
 * @param trade - Trade booking form data
 * @param clientId - Client ID (trading entity)
 * @param tradeId - Firestore document ID of the trade to update
 */
export const updateTrade = async (
  trade: TradeBookingForm,
  clientId: string,
  tradeId: string
): Promise<{ tradeId: string; tradeNumber: string; potentialConfirmationMatch?: PotentialConfirmationMatch }> => {
  logger.log('✏️ [TradeBookingService] Updating trade:', tradeId);

  const tradeData = convertToPascalCase(trade);

  const response = await apiClient.put<{ trade_id: string; trade_number: string; potentialConfirmationMatch?: PotentialConfirmationMatch }>(
    `/api/v1/clients/${clientId}/trades/${tradeId}`,
    tradeData
  );

  logger.log('✅ [TradeBookingService] Trade updated successfully:', response.data);

  return {
    tradeId: response.data.trade_id,
    tradeNumber: response.data.trade_number,
    potentialConfirmationMatch: response.data.potentialConfirmationMatch,
  };
};

/**
 * Promote a Draft trade to Booked, optionally updating fields and attaching allocations.
 * @param trade - Trade booking form data (field updates applied atomically with state transition)
 * @param clientId - Client ID (trading entity)
 * @param tradeId - Firestore document ID of the Draft trade
 * @param allocation - Optional allocation rows; if provided, triggers internal trade generation
 */
export const bookDraft = async (
  trade: TradeBookingForm,
  clientId: string,
  tradeId: string,
  allocation?: AllocationRow[]
): Promise<{ tradeId: string; tradeNumber: string; settlementInstruction?: { attempted: boolean; success: boolean; toEmail: string | null; outputFormat: string | null; errorMessage: string | null }; potentialConfirmationMatch?: PotentialConfirmationMatch }> => {
  logger.log('📋➡️ [TradeBookingService] Booking draft trade:', tradeId, allocation?.length ? `with ${allocation.length} allocation rows` : '');

  const tradeData = convertToPascalCase(trade);

  // Include allocation data if provided
  if (allocation && allocation.length > 0) {
    tradeData.allocation = allocation.map(row => ({
      entityId: row.entityId,
      entityName: row.entityName,
      entityTaxId: row.entityTaxId,
      grouping1: row.grouping1,
      grouping2: row.grouping2,
      grouping3: row.grouping3,
      amount: row.amount,
      percentage: row.percentage,
      hedgeObject: row.hedgeObject,
      notes: row.notes,
    }));
  }

  const response = await apiClient.post<{ trade_id: string; trade_number: string; workflow_state: string; settlementInstruction?: { attempted: boolean; success: boolean; toEmail: string | null; outputFormat: string | null; errorMessage: string | null }; potentialConfirmationMatch?: PotentialConfirmationMatch }>(
    `/api/v1/clients/${clientId}/trades/${tradeId}/book`,
    tradeData
  );

  logger.log('✅ [TradeBookingService] Draft booked successfully:', response.data);

  return {
    tradeId: response.data.trade_id,
    tradeNumber: response.data.trade_number,
    settlementInstruction: response.data.settlementInstruction,
    potentialConfirmationMatch: response.data.potentialConfirmationMatch,
  };
};

/**
 * Get user's draft trades
 * @param clientId - Client ID
 */
export const getDrafts = async (clientId: string): Promise<TradeBookingForm[]> => {
  logger.log('[TradeBookingService] Fetching drafts for client:', clientId);

  // TODO: Implement when GET endpoint is available
  // const response = await apiClient.get<TradeBookingForm[]>(`/clients/${clientId}/trades/drafts`);

  logger.log('[TradeBookingService] getDrafts not yet implemented on backend');
  return [];
};


// ========== Multi-Trade Draft CRUD ==========

/**
 * List all multi-trade drafts for a client (metadata only)
 */
export const listMultiTradeDrafts = async (clientId: string): Promise<MultiTradeDraftSummary[]> => {
  const response = await apiClient.get<MultiTradeDraftSummary[]>(
    `/api/v1/clients/${clientId}/multi-trade-drafts`
  );
  return response.data;
};

/**
 * Get a full multi-trade draft (including trades array)
 */
export const getMultiTradeDraft = async (clientId: string, draftId: string): Promise<MultiTradeDraft> => {
  const response = await apiClient.get<MultiTradeDraft>(
    `/api/v1/clients/${clientId}/multi-trade-drafts/${draftId}`
  );
  return response.data;
};

/**
 * Create a new multi-trade draft
 */
export const createMultiTradeDraft = async (
  clientId: string,
  payload: MultiTradeDraftPayload
): Promise<{ id: string; name: string }> => {
  const response = await apiClient.post<{ id: string; name: string }>(
    `/api/v1/clients/${clientId}/multi-trade-drafts`,
    payload
  );
  return response.data;
};

/**
 * Update an existing multi-trade draft
 */
export const updateMultiTradeDraft = async (
  clientId: string,
  draftId: string,
  payload: MultiTradeDraftPayload
): Promise<void> => {
  await apiClient.put(
    `/api/v1/clients/${clientId}/multi-trade-drafts/${draftId}`,
    payload
  );
};

/**
 * Delete a multi-trade draft
 */
export const deleteMultiTradeDraft = async (clientId: string, draftId: string): Promise<void> => {
  await apiClient.delete(`/api/v1/clients/${clientId}/multi-trade-drafts/${draftId}`);
};

// Returns the 423 response body if the draft is locked by another user, or null on success.
export const lockMultiTradeDraft = async (
  clientId: string,
  draftId: string
): Promise<{ lockedByName: string; lockedUntil: string } | null> => {
  try {
    await apiClient.post(`/api/v1/clients/${clientId}/multi-trade-drafts/${draftId}/lock`);
    return null;
  } catch (err: any) {
    if (err?.response?.status === 423) {
      return err.response.data?.detail ?? { lockedByName: 'Another user', lockedUntil: '' };
    }
    throw err;
  }
};

export const unlockMultiTradeDraft = async (clientId: string, draftId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/v1/clients/${clientId}/multi-trade-drafts/${draftId}/lock`);
  } catch {
    // Best-effort — swallow errors (e.g. network gone on tab close)
  }
};
